<template>
  <!-- The async-setup screen is swapped in at the root once the org probe
       resolves; a stable root element keeps the route <Suspense> anchor
       valid (see .stable-route-root in global.scss). -->
  <div class="stable-route-root">
    <NotFound
      v-if="notFound"
      description="This automation run does not exist or you do not have access to it."
    />
    <AutomationRunDetail
      v-else-if="resolvedOrgId"
      :run-id="runId"
      :organization-id="resolvedOrgId"
    />
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, ref, watch, type PropType } from "vue";
import { useRoute } from "vue-router";

import type { AutomationRunId, OrgId } from "@ctrl/controller-rpc";

import AutomationRunDetail from "@/components/ai/AutomationRunDetail.vue";
import * as inject from "@/components/inject";
import NotFound from "@/components/NotFound.vue";

export default defineComponent({
  name: "AutomationRunRoute",
  components: { AutomationRunDetail, NotFound },
  props: {
    /** The automation id, from the route path. */
    objectId: { type: String, required: true },
    runId: { type: String as PropType<AutomationRunId>, required: true },
    organizationId: { type: String as PropType<OrgId>, default: null },
  },
  setup(props) {
    const route = useRoute();
    const controller = inject.controller();
    const profile = inject.profile();

    // `AutomationRunDetail` requires the owning organization, but deep links
    // to runs — notably the notice links the controller creates when a run
    // pauses for user input — are plain `/automations/<id>/runs/<runId>`
    // paths without it, and mounting the detail screen without an org id
    // fatals on its first RPC (FRONTEND-FH5). Take the org from the prop or
    // query when present, otherwise find which of the user's organizations
    // owns the automation.
    const explicitOrgId = computed(
      () =>
        props.organizationId ||
        (typeof route.query.organizationId === "string"
          ? route.query.organizationId
          : null)
    );
    const resolvedOrgId = ref<OrgId | null>(null);
    const notFound = ref(false);

    let probe = 0;
    watch(
      () => [props.objectId, explicitOrgId.value] as const,
      async ([automationId, explicit]) => {
        const current = ++probe;
        notFound.value = false;
        if (explicit) {
          resolvedOrgId.value = explicit;
          return;
        }
        resolvedOrgId.value = null;
        for (const organization of profile.value.organizations) {
          const automationIds =
            (await controller.ai.automation.all(organization.id)).release() ??
            [];
          // A newer probe superseded this one while we were awaiting.
          if (current !== probe) return;
          if (automationIds.includes(automationId)) {
            resolvedOrgId.value = organization.id;
            return;
          }
        }
        notFound.value = true;
      },
      { immediate: true }
    );

    return { resolvedOrgId, notFound };
  },
});
</script>
